import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';
import type { Attachment, ChatLabels } from '../types';
import { cx, formatBytes, matchesAccept, uid } from '../utils';
import { AttachmentChip } from './AttachmentChip';
import { PaperclipIcon, SendIcon, StopIcon } from './icons';

const MAX_TEXTAREA_HEIGHT = 140;

export interface ComposerHandle {
  focus: () => void;
  setText: (text: string) => void;
}

interface ComposerProps {
  labels: ChatLabels;
  disabled: boolean;
  allowAttachments: boolean;
  accept?: string;
  maxFileSize: number;
  maxFiles: number;
  handle: Ref<ComposerHandle>;
  onSend: (text: string, attachments: Attachment[]) => void;
  /**
   * Abort the turn in flight. When supplied, the send button becomes a stop
   * button while a reply is pending.
   *
   * Optional so the composer still works without it, but a backend whose turns run
   * for minutes rather than seconds needs it: without a stop button the only way out
   * of a slow turn is to close the panel or reload, and both leave the request
   * running server-side.
   */
  onStop?: () => void;
}

export function Composer({
  labels,
  disabled,
  allowAttachments,
  accept,
  maxFileSize,
  maxFiles,
  handle,
  onSend,
  onStop,
}: ComposerProps) {
  const [text, setText] = useState('');
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(handle, () => ({
    focus: () => textareaRef.current?.focus(),
    setText: (value: string) => {
      setText(value);
      requestAnimationFrame(() => textareaRef.current?.focus());
    },
  }));

  // Auto-grow the textarea up to a ceiling, then scroll.
  useEffect(() => {
    const element = textareaRef.current;
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`;
  }, [text]);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const incoming = Array.from(files);
      if (incoming.length === 0) return;

      const accepted: Attachment[] = [];
      let message: string | null = null;

      for (const file of incoming) {
        if (attachments.length + accepted.length >= maxFiles) {
          message = `${labels.tooManyFiles} (max ${maxFiles})`;
          break;
        }
        if (!matchesAccept(file, accept)) {
          message = `${labels.fileTypeRejected}: ${file.name}`;
          continue;
        }
        if (file.size > maxFileSize) {
          message = `${labels.fileTooLarge}: ${file.name} (max ${formatBytes(maxFileSize)})`;
          continue;
        }
        accepted.push({
          id: uid('f'),
          name: file.name,
          size: file.size,
          mimeType: file.type || 'application/octet-stream',
          file,
          status: 'ready',
        });
      }

      if (accepted.length) setAttachments((prev) => [...prev, ...accepted]);
      setError(message);
    },
    [accept, attachments.length, labels, maxFileSize, maxFiles],
  );

  const onFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) addFiles(event.target.files);
    event.target.value = '';
  };

  const submit = () => {
    if (disabled) return;
    const trimmed = text.trim();
    if (!trimmed && attachments.length === 0) return;
    onSend(trimmed, attachments);
    setText('');
    setAttachments([]);
    setError(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (allowAttachments && event.dataTransfer.files.length) addFiles(event.dataTransfer.files);
  };

  const canSend = !disabled && (text.trim() !== '' || attachments.length > 0);

  return (
    <div
      className={cx('cbw-composer', dragging && 'is-dragging')}
      onDragOver={(event) => {
        if (!allowAttachments) return;
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      {error && (
        <div className="cbw-composer__error" role="alert">
          {error}
        </div>
      )}

      {attachments.length > 0 && (
        <div className="cbw-composer__attachments" aria-label={labels.attachmentsLabel}>
          {attachments.map((attachment) => (
            <AttachmentChip
              key={attachment.id}
              attachment={attachment}
              removeLabel={labels.removeAttachment}
              onRemove={() =>
                setAttachments((prev) => prev.filter((item) => item.id !== attachment.id))
              }
            />
          ))}
        </div>
      )}

      <div className="cbw-composer__row">
        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={accept}
              className="cbw-sr-only"
              onChange={onFileInputChange}
              tabIndex={-1}
            />
            <button
              type="button"
              className="cbw-iconbtn"
              onClick={() => fileInputRef.current?.click()}
              aria-label={labels.attach}
              title={labels.attach}
              disabled={disabled}
            >
              <PaperclipIcon />
            </button>
          </>
        )}

        <textarea
          ref={textareaRef}
          className="cbw-composer__input"
          rows={1}
          value={text}
          placeholder={labels.inputPlaceholder}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          aria-label={labels.inputPlaceholder}
        />

        {/* While a reply is pending the same slot becomes a stop button. Same
            position rather than an extra control, so the visitor's pointer is
            already where it needs to be, and the button is never simply dead. */}
        {disabled && onStop ? (
          <button
            type="button"
            className="cbw-sendbtn cbw-sendbtn--stop"
            onClick={onStop}
            aria-label={labels.stop}
            title={labels.stop}
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            className="cbw-sendbtn"
            onClick={submit}
            disabled={!canSend}
            aria-label={labels.send}
            title={labels.send}
          >
            <SendIcon />
          </button>
        )}
      </div>
    </div>
  );
}
